/**
 * UpgradeLibrary - Unified upgrade system combining relics and piece upgrades
 * All bonuses are now in one category with rarity-based distribution
 */

import { PieceUpgrade } from '../../types';

/**
 * Context requirement for an upgrade to be offered. Keeps dead picks out of
 * reward rolls (e.g. queen upgrades with no queen, wall upgrades before
 * obstacles exist, puzzle upgrades with no puzzles left on the map).
 */
export type UpgradeRequirement =
  | 'captured'      // at least one captured piece to revive
  | 'queen' | 'rook' | 'bishop' | 'knight' | 'pawn' // piece of that type in army
  | 'bishops2'      // two or more bishops
  | 'obstacles'     // floors with water/wall complications (floor 3+)
  | 'teleporters'   // teleporter pads can appear (floor 5+) or player can place them
  | 'puzzles';      // unvisited puzzle nodes remain on the map

export interface Upgrade {
  id: string;
  name: string;
  description: string;
  rarity: 'common' | 'uncommon' | 'rare' | 'legendary';
  requires?: UpgradeRequirement;
  effect: {
    type: 'army_add' | 'army_revive' | 'gold' | 'movement' | 'ability';
    value: any;
    target?: string;
  };
}

/**
 * Whether owning multiple copies of an upgrade is meaningful.
 * Army additions, revives and gold bonuses stack; boolean abilities and
 * movement rules do not (the engine checks them as flags).
 */
export function isStackable(upgrade: Upgrade): boolean {
  return upgrade.effect.type === 'army_add'
    || upgrade.effect.type === 'army_revive'
    || upgrade.effect.type === 'gold';
}

export interface OfferContext {
  floor: number;
  ownedIds: Set<string>;
  pieceCounts: Map<string, number>; // piece type -> count in player army
  capturedCount: number;
  puzzlesRemaining: boolean;
  tier?: 'normal' | 'elite' | 'boss';
}

export const UPGRADE_LIBRARY: Record<string, Upgrade> = {
  // ==================== COMMON (60%) ====================
  // Army additions
  'add_pawn': {
    id: 'add_pawn',
    name: 'Recruit Pawn',
    description: 'Add 1 pawn to your army',
    rarity: 'common',
    effect: { type: 'army_add', value: 'p', target: 'army' },
  },
  'add_two_pawns': {
    id: 'add_two_pawns',
    name: 'Recruit Infantry',
    description: 'Add 2 pawns to your army',
    rarity: 'common',
    effect: { type: 'army_add', value: 'pp', target: 'army' },
  },
  'revive_piece': {
    id: 'revive_piece',
    name: 'Resurrection',
    description: 'Revive one random captured piece',
    rarity: 'common',
    requires: 'captured',
    effect: { type: 'army_revive', value: 1, target: 'captured' },
  },

  // Gold bonuses
  'small_gold_bonus': {
    id: 'small_gold_bonus',
    name: 'Small Purse',
    description: 'Gain 25 gold immediately',
    rarity: 'common',
    effect: { type: 'gold', value: 25, target: 'immediate' },
  },
  'gold_per_battle': {
    id: 'gold_per_battle',
    name: 'Steady Income',
    description: 'Gain +5 gold after each battle',
    rarity: 'common',
    effect: { type: 'gold', value: 5, target: 'per_battle' },
  },
  'medium_gold_bonus': {
    id: 'medium_gold_bonus',
    name: 'Small Fortune',
    description: 'Gain 45 gold immediately',
    rarity: 'common',
    effect: { type: 'gold', value: 45, target: 'immediate' },
  },

  // Capture-based gold
  'bounty_hunter': {
    id: 'bounty_hunter',
    name: 'Bounty Hunter',
    description: 'Earn +3 gold for each piece you capture',
    rarity: 'common',
    effect: { type: 'ability', value: 'bounty_hunter', target: 'player' },
  },
  'scavenger': {
    id: 'scavenger',
    name: 'Scavenger',
    description: 'Earn +1 gold for each pawn you capture',
    rarity: 'common',
    effect: { type: 'ability', value: 'scavenger', target: 'player' },
  },
  'puzzle_veteran': {
    id: 'puzzle_veteran',
    name: 'Puzzle Veteran',
    description: '+10 gold for completing puzzle levels',
    rarity: 'common',
    requires: 'puzzles',
    effect: { type: 'ability', value: 'puzzle_veteran', target: 'player' },
  },

  // Clock bonuses
  'blitz_instinct': {
    id: 'blitz_instinct',
    name: 'Blitz Instinct',
    description: '+3 seconds on your clock each time you capture a piece',
    rarity: 'common',
    effect: { type: 'ability', value: 'clock_per_capture_3', target: 'player' },
  },
  'war_profiteer': {
    id: 'war_profiteer',
    name: 'War Profiteer',
    description: '+1 gold for each turn the battle lasts',
    rarity: 'common',
    effect: { type: 'ability', value: 'war_profiteer', target: 'player' },
  },
  'bounty_board': {
    id: 'bounty_board',
    name: 'Bounty Board',
    description: 'A random enemy piece is marked at battle start; capturing it gives +15 gold',
    rarity: 'common',
    effect: { type: 'ability', value: 'bounty_board', target: 'player' },
  },

  // Minor movement bonuses
  'cornered_king': {
    id: 'cornered_king',
    name: 'Cornered King',
    description: 'King on the board edge gets +1 movement range',
    rarity: 'common',
    effect: { type: 'movement', value: 'cornered_king', target: 'king' },
  },
  'pawn_shield_wall': {
    id: 'pawn_shield_wall',
    name: 'Pawn Shield Wall',
    description: 'Pawns can move one square sideways (no capture)',
    rarity: 'common',
    requires: 'pawn',
    effect: { type: 'movement', value: 'pawn_lateral_move', target: 'pawn' },
  },
  'pawn_retreat': {
    id: 'pawn_retreat',
    name: 'Tactical Retreat',
    description: 'Pawns can move backward one square (no capture)',
    rarity: 'common',
    requires: 'pawn',
    effect: { type: 'movement', value: 'backward_move', target: 'pawn' },
  },
  'king_extra_step': {
    id: 'king_extra_step',
    name: 'Royal Stride',
    description: 'King can move one extra square',
    rarity: 'common',
    effect: { type: 'movement', value: 'king_range_1', target: 'king' },
  },

  // Minor abilities
  'sidestep_once': {
    id: 'sidestep_once',
    name: 'Sidestep',
    description: 'Any piece can move one square sideways once per battle',
    rarity: 'common',
    effect: { type: 'movement', value: 'lateral_once', target: 'all' },
  },

  // Center control
  'center_control': {
    id: 'center_control',
    name: 'Center Control',
    description: 'Your pieces on the center 4 squares cannot be captured by pawns',
    rarity: 'common',
    effect: { type: 'ability', value: 'center_pawn_immunity', target: 'all' },
  },

  // ==================== UNCOMMON (25%) ====================
  // Army additions
  'add_knight': {
    id: 'add_knight',
    name: 'Recruit Knight',
    description: 'Add 1 knight to your army',
    rarity: 'uncommon',
    effect: { type: 'army_add', value: 'n', target: 'army' },
  },
  'add_bishop': {
    id: 'add_bishop',
    name: 'Recruit Bishop',
    description: 'Add 1 bishop to your army',
    rarity: 'uncommon',
    effect: { type: 'army_add', value: 'b', target: 'army' },
  },

  // Better gold bonuses
  'large_gold_bonus': {
    id: 'large_gold_bonus',
    name: 'Treasure Chest',
    description: 'Gain 75 gold immediately',
    rarity: 'uncommon',
    effect: { type: 'gold', value: 75, target: 'immediate' },
  },
  'gold_multiplier': {
    id: 'gold_multiplier',
    name: 'Golden Touch',
    description: 'Earn 50% more gold from battles',
    rarity: 'uncommon',
    effect: { type: 'gold', value: 1.5, target: 'multiplier' },
  },
  'large_gold_per_battle': {
    id: 'large_gold_per_battle',
    name: 'Wealthy Patron',
    description: 'Gain +15 gold after each battle',
    rarity: 'uncommon',
    effect: { type: 'gold', value: 15, target: 'per_battle' },
  },

  // Capture-based economy
  'trophy_collector': {
    id: 'trophy_collector',
    name: 'Trophy Collector',
    description: 'Earn +8 gold for capturing a knight, bishop, or rook',
    rarity: 'uncommon',
    effect: { type: 'ability', value: 'trophy_collector', target: 'player' },
  },
  'battle_momentum': {
    id: 'battle_momentum',
    name: 'Battle Momentum',
    description: '+5 seconds on your clock each time you capture a piece',
    rarity: 'uncommon',
    effect: { type: 'ability', value: 'clock_per_capture_5', target: 'player' },
  },
  'insurance': {
    id: 'insurance',
    name: 'Insurance',
    description: 'When you lose a piece, gain gold (pawn=2, knight/bishop=5, rook=8, queen=15)',
    rarity: 'uncommon',
    effect: { type: 'ability', value: 'gold_on_piece_loss', target: 'player' },
  },
  'speed_bonus': {
    id: 'speed_bonus',
    name: 'Speed Bonus',
    description: 'Win in under 15 moves: +20 gold',
    rarity: 'uncommon',
    effect: { type: 'ability', value: 'speed_bonus', target: 'player' },
  },
  'survivor_pay': {
    id: 'survivor_pay',
    name: "Survivor's Pay",
    description: '+2 gold for each of your pieces still alive when you win',
    rarity: 'uncommon',
    effect: { type: 'ability', value: 'survivor_pay', target: 'player' },
  },

  // Rook movement
  'rook_lift': {
    id: 'rook_lift',
    name: 'Rook Lift',
    description: 'Once per battle, a rook can move one square diagonally',
    rarity: 'uncommon',
    requires: 'rook',
    effect: { type: 'movement', value: 'rook_diagonal_once', target: 'rook' },
  },

  // Positional/defensive
  'formation': {
    id: 'formation',
    name: 'Formation',
    description: 'Pieces adjacent to a friendly piece survive the first capture attempt each battle',
    rarity: 'uncommon',
    effect: { type: 'ability', value: 'formation_shield', target: 'all' },
  },

  // Economy
  'knight_fork_bonus': {
    id: 'knight_fork_bonus',
    name: 'Knight Fork Bonus',
    description: '+5 gold when your knight attacks 2 or more enemy pieces',
    rarity: 'uncommon',
    requires: 'knight',
    effect: { type: 'ability', value: 'knight_fork_bonus', target: 'player' },
  },
  'pressure_play': {
    id: 'pressure_play',
    name: 'Pressure Play',
    description: 'Enemy loses 3 seconds each time you give check',
    rarity: 'uncommon',
    effect: { type: 'ability', value: 'pressure_play', target: 'player' },
  },
  'endgame_specialist': {
    id: 'endgame_specialist',
    name: 'Endgame Specialist',
    description: '+30 gold if you win with all your pieces alive',
    rarity: 'uncommon',
    effect: { type: 'ability', value: 'endgame_specialist', target: 'player' },
  },

  // Movement
  'bishop_pair': {
    id: 'bishop_pair',
    name: 'Bishop Pair',
    description: 'If you have 2+ bishops, each gets one knight-jump once per battle',
    rarity: 'uncommon',
    requires: 'bishops2',
    effect: { type: 'movement', value: 'bishop_pair_jump', target: 'bishop' },
  },

  // Positional defense
  'sacred_ground': {
    id: 'sacred_ground',
    name: 'Sacred Ground',
    description: 'A random square is marked at battle start; your pieces on it cannot be captured',
    rarity: 'uncommon',
    effect: { type: 'ability', value: 'sacred_ground', target: 'map' },
  },

  // Reflecting bishop
  'reflecting_bishop': {
    id: 'reflecting_bishop',
    name: 'Reflecting Bishop',
    description: 'Bishops bounce off the board edge and continue in the reflected direction',
    rarity: 'uncommon',
    requires: 'bishop',
    effect: { type: 'movement', value: 'bishop_reflect', target: 'bishop' },
  },

  // Better movement abilities
  'pawn_lateral_capture': {
    id: 'pawn_lateral_capture',
    name: 'Side Strike',
    description: 'Pawns can capture left or right (not just diagonal)',
    rarity: 'uncommon',
    requires: 'pawn',
    effect: { type: 'movement', value: 'lateral_capture', target: 'pawn' },
  },
  'knight_phase_jump': {
    id: 'knight_phase_jump',
    name: 'Phase Jump',
    description: 'Knights can move to any square along their L-shaped path',
    rarity: 'uncommon',
    requires: 'knight',
    effect: { type: 'movement', value: 'knight_slide', target: 'knight' },
  },

  // Decent abilities
  'pawn_immunity': {
    id: 'pawn_immunity',
    name: 'Pawn Shield',
    description: 'Your pieces cannot be captured by enemy pawns',
    rarity: 'uncommon',
    effect: { type: 'ability', value: 'pawn_immunity', target: 'all' },
  },
  'double_advance': {
    id: 'double_advance',
    name: 'Pawn Rush',
    description: 'Pawns can double-advance once per battle from any square',
    rarity: 'uncommon',
    requires: 'pawn',
    effect: { type: 'movement', value: 'double_advance_once', target: 'pawn' },
  },
  'bishop_color_switch': {
    id: 'bishop_color_switch',
    name: 'Color Mastery',
    description: 'Bishops can move one square orthogonally once per battle to switch colors',
    rarity: 'uncommon',
    requires: 'bishop',
    effect: { type: 'movement', value: 'color_switch_1', target: 'bishop' },
  },

  // ==================== RARE (12%) ====================
  // Army additions
  'add_rook': {
    id: 'add_rook',
    name: 'Recruit Rook',
    description: 'Add 1 rook to your army',
    rarity: 'rare',
    effect: { type: 'army_add', value: 'r', target: 'army' },
  },
  'revive_two_pieces': {
    id: 'revive_two_pieces',
    name: 'Mass Resurrection',
    description: 'Revive two random captured pieces',
    rarity: 'rare',
    requires: 'captured',
    effect: { type: 'army_revive', value: 2, target: 'captured' },
  },

  // Strong gold bonuses
  'huge_gold_bonus': {
    id: 'huge_gold_bonus',
    name: 'Dragon Hoard',
    description: 'Gain 125 gold immediately',
    rarity: 'rare',
    effect: { type: 'gold', value: 125, target: 'immediate' },
  },
  'gold_double': {
    id: 'gold_double',
    name: 'Midas Touch',
    description: 'Earn double gold from battles',
    rarity: 'rare',
    effect: { type: 'gold', value: 2.0, target: 'multiplier' },
  },

  // Strong movement abilities
  'queen_teleport': {
    id: 'queen_teleport',
    name: 'Royal Teleport',
    description: 'Queen can teleport to any empty square once per battle',
    rarity: 'rare',
    requires: 'queen',
    effect: { type: 'movement', value: 'teleport_any', target: 'queen' },
  },
  'knight_double_move': {
    id: 'knight_double_move',
    name: 'Chain Leap',
    description: 'The first time a knight captures each turn, it can move again',
    rarity: 'rare',
    requires: 'knight',
    effect: { type: 'ability', value: 'extra_move_first_capture', target: 'knight' },
  },
  'bishop_phase': {
    id: 'bishop_phase',
    name: 'Phase Shift',
    description: 'Bishops can pass through pieces when moving',
    rarity: 'rare',
    requires: 'bishop',
    effect: { type: 'movement', value: 'phase_through', target: 'bishop' },
  },
  'rook_castle_anywhere': {
    id: 'rook_castle_anywhere',
    name: 'Mobile Fortress',
    description: 'Rooks can swap positions with your king once per battle',
    rarity: 'rare',
    requires: 'rook',
    effect: { type: 'ability', value: 'rook_king_swap_once', target: 'rook' },
  },

  // Strong abilities
  'king_protection_aura': {
    id: 'king_protection_aura',
    name: 'Royal Guard',
    description: 'Pieces adjacent to your king cannot be captured',
    rarity: 'rare',
    effect: { type: 'ability', value: 'king_aura', target: 'king' },
  },
  'knight_outpost': {
    id: 'knight_outpost',
    name: 'Knight Outpost',
    description: 'Knights that are protected by a friendly pawn cannot be captured',
    rarity: 'rare',
    requires: 'knight',
    effect: { type: 'ability', value: 'knight_outpost', target: 'knight' },
  },
  'conscription': {
    id: 'conscription',
    name: 'Conscription',
    description: 'When you capture an enemy pawn, it joins your army for the next battle',
    rarity: 'rare',
    effect: { type: 'ability', value: 'conscription', target: 'player' },
  },
  'sacrifice_play': {
    id: 'sacrifice_play',
    name: 'Sacrifice Play',
    description: 'Once per battle, sacrifice a piece to capture all enemy pieces adjacent to it',
    rarity: 'rare',
    effect: { type: 'ability', value: 'sacrifice_play', target: 'player' },
  },
  'desperate_times': {
    id: 'desperate_times',
    name: 'Desperate Times',
    description: 'When you have fewer pieces than the enemy, all your pieces get +1 movement range',
    rarity: 'rare',
    effect: { type: 'movement', value: 'desperate_times', target: 'all' },
  },
  'ambush': {
    id: 'ambush',
    name: 'Ambush',
    description: "Once per battle, a piece that hasn't moved yet can capture any enemy within 2 squares",
    rarity: 'rare',
    effect: { type: 'ability', value: 'ambush', target: 'all' },
  },
  'bloodlust': {
    id: 'bloodlust',
    name: 'Bloodlust',
    description: 'After capturing, your piece cannot be captured until next turn',
    rarity: 'rare',
    effect: { type: 'ability', value: 'bloodlust', target: 'all' },
  },
  'quick_draw': {
    id: 'quick_draw',
    name: 'Quick Draw',
    description: 'Your first move each battle does not cost a turn — the enemy does not respond',
    rarity: 'rare',
    effect: { type: 'ability', value: 'quick_draw', target: 'player' },
  },
  'queen_gambit': {
    id: 'queen_gambit',
    name: "Queen's Gambit",
    description: 'Once per battle, your queen can swap positions with an adjacent friendly piece',
    rarity: 'rare',
    requires: 'queen',
    effect: { type: 'ability', value: 'queen_swap_once', target: 'queen' },
  },

  // Defensive
  'tower_defense': {
    id: 'tower_defense',
    name: 'Tower Defense',
    description: 'Rooks on your back rank cannot be captured',
    rarity: 'common',
    requires: 'rook',
    effect: { type: 'ability', value: 'tower_defense', target: 'rook' },
  },

  // ==================== LEGENDARY (3%) ====================
  // Turn manipulation
  'counterattack': {
    id: 'counterattack',
    name: 'Counterattack',
    description: 'When the enemy captures one of your pieces, you immediately get an extra turn (twice per battle)',
    rarity: 'legendary',
    effect: { type: 'ability', value: 'counterattack', target: 'player' },
  },

  // Army/capture
  'conscript_officer': {
    id: 'conscript_officer',
    name: 'Conscript Officer',
    description: 'Capturing a knight, bishop, or rook converts it to a pawn on your side',
    rarity: 'legendary',
    effect: { type: 'ability', value: 'conscript_officer', target: 'player' },
  },

  // Army additions
  'add_queen': {
    id: 'add_queen',
    name: 'Recruit Queen',
    description: 'Add 1 queen to your army',
    rarity: 'legendary',
    effect: { type: 'army_add', value: 'q', target: 'army' },
  },
  'add_king': {
    id: 'add_king',
    name: 'Second Monarch',
    description: 'Add a second king to your army (both must be checkmated to lose)',
    rarity: 'legendary',
    effect: { type: 'army_add', value: 'k', target: 'army' },
  },

  // Powerful abilities
  'double_turn': {
    id: 'double_turn',
    name: 'Time Bender',
    description: 'Take 2 consecutive turns at the start of each battle',
    rarity: 'legendary',
    effect: { type: 'ability', value: 'double_start_turns', target: 'player' },
  },
  'king_queen_move': {
    id: 'king_queen_move',
    name: 'Royal Reach',
    description: 'King can move like a queen once per battle',
    rarity: 'legendary',
    effect: { type: 'ability', value: 'king_queen_once', target: 'king' },
  },
  'teleport_to_king': {
    id: 'teleport_to_king',
    name: 'Dimensional Anchor',
    description: 'All pieces can teleport adjacent to king once per battle',
    rarity: 'legendary',
    effect: { type: 'ability', value: 'teleport_king', target: 'all' },
  },
  'capture_stun': {
    id: 'capture_stun',
    name: 'Mirror Shield',
    description: 'When an enemy captures your piece, that enemy piece is stunned and cannot move on its next turn',
    rarity: 'legendary',
    effect: { type: 'ability', value: 'stun_capturer', target: 'all' },
  },

  // ==================== COMPLICATION INTERACTIONS ====================

  // === WATER (Uncommon/Rare) ===
  'water_walking': {
    id: 'water_walking',
    name: 'Water Walking',
    description: 'Your pieces can move through water squares',
    rarity: 'uncommon',
    requires: 'obstacles',
    effect: { type: 'ability', value: 'water_immunity', target: 'all' },
  },
  'flood_enemy': {
    id: 'flood_enemy',
    name: 'Flood',
    description: 'Place 1 water square on enemy side at battle start',
    rarity: 'rare',
    requires: 'obstacles',
    effect: { type: 'ability', value: 'place_water_1', target: 'map' },
  },
  'water_control': {
    id: 'water_control',
    name: 'Tidal Control',
    description: 'Once per battle, move a water square to any empty square',
    rarity: 'legendary',
    requires: 'obstacles',
    effect: { type: 'ability', value: 'move_water', target: 'map' },
  },

  // === WALLS (Common/Uncommon/Rare/Legendary) ===
  'wall_breaker': {
    id: 'wall_breaker',
    name: 'Wall Breaker',
    description: 'Destroy one wall per battle',
    rarity: 'common',
    requires: 'obstacles',
    effect: { type: 'ability', value: 'break_wall_once', target: 'all' },
  },
  'wall_phase': {
    id: 'wall_phase',
    name: 'Wall Phasing',
    description: 'Your pieces can move through walls',
    rarity: 'uncommon',
    requires: 'obstacles',
    effect: { type: 'ability', value: 'wall_immunity', target: 'all' },
  },
  'wall_builder': {
    id: 'wall_builder',
    name: 'Fortification',
    description: 'Place 1 wall on your side at battle start',
    rarity: 'rare',
    requires: 'obstacles',
    effect: { type: 'ability', value: 'place_walls_1', target: 'map' },
  },
  'wall_master': {
    id: 'wall_master',
    name: 'Master Builder',
    description: 'Once per battle, place or remove a wall',
    rarity: 'legendary',
    requires: 'obstacles',
    effect: { type: 'ability', value: 'wall_control', target: 'map' },
  },

  // === OBSTACLE MANIPULATION (Uncommon) ===
  'wall_mover': {
    id: 'wall_mover',
    name: 'Stone Shaper',
    description: 'Spend your turn to move a wall to a new square. Cannot use while in check.',
    rarity: 'uncommon',
    requires: 'obstacles',
    effect: { type: 'ability', value: 'turn_move_wall', target: 'map' },
  },
  'water_mover': {
    id: 'water_mover',
    name: 'Tide Caller',
    description: 'Spend your turn to move a water square. Cannot use while in check.',
    rarity: 'uncommon',
    requires: 'obstacles',
    effect: { type: 'ability', value: 'turn_move_water', target: 'map' },
  },

  // === TELEPORTERS (Common/Uncommon/Rare/Legendary) ===
  'extra_teleport': {
    id: 'extra_teleport',
    name: 'Teleport Charge',
    description: '+1 teleporter use per battle',
    rarity: 'common',
    requires: 'teleporters',
    effect: { type: 'ability', value: 'teleport_uses_1', target: 'all' },
  },
  'bidirectional_teleport': {
    id: 'bidirectional_teleport',
    name: 'Two-Way Portal',
    description: 'Teleporters work in both directions',
    rarity: 'uncommon',
    requires: 'teleporters',
    effect: { type: 'ability', value: 'teleport_bidirectional', target: 'map' },
  },
  'place_teleporter': {
    id: 'place_teleporter',
    name: 'Portal Summoning',
    description: 'Place your own teleporter pair at battle start',
    rarity: 'rare',
    requires: 'obstacles',
    effect: { type: 'ability', value: 'place_teleporter', target: 'map' },
  },
  'infinite_teleport': {
    id: 'infinite_teleport',
    name: 'Infinite Portals',
    description: 'Unlimited teleporter uses',
    rarity: 'legendary',
    requires: 'teleporters',
    effect: { type: 'ability', value: 'teleport_unlimited', target: 'all' },
  },

  // ==================== MAP INTERACTIONS ====================

  'skip_node': {
    id: 'skip_node',
    name: 'Shortcut',
    description: 'Skip one battle node once per act (no rewards)',
    rarity: 'rare',
    effect: { type: 'ability', value: 'skip_battle_once', target: 'map' },
  },
  'jump_node': {
    id: 'jump_node',
    name: 'Dimension Door',
    description: 'Once per act, jump to any unconnected node on current level',
    rarity: 'legendary',
    effect: { type: 'ability', value: 'jump_to_any_node', target: 'map' },
  },

  // ==================== CHESS CLOCK INTERACTIONS ====================

  'time_bonus_small': {
    id: 'time_bonus_small',
    name: 'Quick Thinking',
    description: '+10 seconds to your clock per turn',
    rarity: 'common',
    effect: { type: 'ability', value: 'time_bonus_10', target: 'player' },
  },
  'time_bonus_medium': {
    id: 'time_bonus_medium',
    name: 'Time Extension',
    description: '+30 seconds to your clock per turn',
    rarity: 'uncommon',
    effect: { type: 'ability', value: 'time_bonus_30', target: 'player' },
  },
  'time_pressure': {
    id: 'time_pressure',
    name: 'Time Pressure',
    description: 'Enemy loses 10 seconds per turn',
    rarity: 'rare',
    effect: { type: 'ability', value: 'enemy_time_loss_10', target: 'enemy' },
  },
  'no_time_limit': {
    id: 'no_time_limit',
    name: 'Timeless',
    description: 'No time limit for your moves',
    rarity: 'legendary',
    effect: { type: 'ability', value: 'unlimited_time', target: 'player' },
  },

  // ==================== PUZZLE INTERACTIONS ====================

  'puzzle_time_small': {
    id: 'puzzle_time_small',
    name: 'Puzzle Focus',
    description: '+15 seconds for puzzle battles',
    rarity: 'common',
    requires: 'puzzles',
    effect: { type: 'ability', value: 'puzzle_time_15', target: 'player' },
  },
  'puzzle_hint': {
    id: 'puzzle_hint',
    name: 'Puzzle Insight',
    description: 'Puzzles start with a free hint revealing which piece to move',
    rarity: 'uncommon',
    requires: 'puzzles',
    effect: { type: 'ability', value: 'puzzle_hint', target: 'player' },
  },
  'puzzle_reveal': {
    id: 'puzzle_reveal',
    name: 'Solution Vision',
    description: 'One free retry per puzzle if you play a wrong move',
    rarity: 'uncommon',
    requires: 'puzzles',
    effect: { type: 'ability', value: 'puzzle_redo_once', target: 'player' },
  },
  'puzzle_skip': {
    id: 'puzzle_skip',
    name: 'Puzzle Master',
    description: 'Puzzles gain an Auto-Solve button (reduced gold reward)',
    rarity: 'rare',
    requires: 'puzzles',
    effect: { type: 'ability', value: 'auto_solve_puzzle', target: 'player' },
  },

  // ==================== ENGINE-BACKED ADDITIONS ====================

  'extended_leap': {
    id: 'extended_leap',
    name: 'Extended Leap',
    description: 'Knights can also jump in a long (3,1) L-shape',
    rarity: 'uncommon',
    requires: 'knight',
    effect: { type: 'movement', value: 'knight_extended', target: 'knight' },
  },
  'minor_phase': {
    id: 'minor_phase',
    name: 'Minor Phase',
    description: 'Once per battle, a bishop can pass through one piece',
    rarity: 'common',
    requires: 'bishop',
    effect: { type: 'movement', value: 'phase_once', target: 'bishop' },
  },
  'shallow_waters': {
    id: 'shallow_waters',
    name: 'Shallow Waters',
    description: 'Your pieces can cross water during the first 3 turns of battle',
    rarity: 'common',
    requires: 'obstacles',
    effect: { type: 'ability', value: 'water_immune_3_turns', target: 'all' },
  },
  'temporal_stasis': {
    id: 'temporal_stasis',
    name: 'Temporal Stasis',
    description: "The enemy's clock keeps running during the first 5 turns of battle",
    rarity: 'rare',
    effect: { type: 'ability', value: 'freeze_enemy_clock_5', target: 'enemy' },
  },
  'perfect_harmony': {
    id: 'perfect_harmony',
    name: 'Perfect Harmony',
    description: 'Your pieces can also move like adjacent friendly pieces',
    rarity: 'legendary',
    effect: { type: 'movement', value: 'share_movement', target: 'all' },
  },
  'phasing_cloak': {
    id: 'phasing_cloak',
    name: 'Phasing Cloak',
    description: 'Your sliding pieces (bishops, rooks, queens) pass through other pieces',
    rarity: 'legendary',
    effect: { type: 'movement', value: 'phase_all', target: 'all' },
  },
};

/**
 * Get all upgrades by rarity
 */
export function getUpgradesByRarity(rarity: 'common' | 'uncommon' | 'rare' | 'legendary'): Upgrade[] {
  return Object.values(UPGRADE_LIBRARY).filter(upgrade => upgrade.rarity === rarity);
}

/**
 * Get a random upgrade weighted by rarity
 * Common: 60%, Uncommon: 25%, Rare: 12%, Legendary: 3%
 */
export function getRandomUpgrade(): Upgrade {
  const roll = Math.random() * 100;
  let rarity: 'common' | 'uncommon' | 'rare' | 'legendary';

  if (roll < 60) {
    rarity = 'common';
  } else if (roll < 85) {
    rarity = 'uncommon';
  } else if (roll < 97) {
    rarity = 'rare';
  } else {
    rarity = 'legendary';
  }

  const upgradesOfRarity = getUpgradesByRarity(rarity);
  const randomIndex = Math.floor(Math.random() * upgradesOfRarity.length);
  return upgradesOfRarity[randomIndex];
}

/**
 * Get multiple random unique upgrades for selection
 */
export function getRandomUpgradeChoices(count: number = 3): Upgrade[] {
  const choices: Upgrade[] = [];
  const allUpgradeIds = Object.keys(UPGRADE_LIBRARY);

  let guard = 0;
  while (choices.length < Math.min(count, allUpgradeIds.length) && guard++ < 500) {
    const upgrade = getRandomUpgrade();
    if (!choices.find(c => c.id === upgrade.id)) {
      choices.push(upgrade);
    }
  }

  return choices;
}

/**
 * Whether an upgrade is sensible to offer in the given run context.
 */
export function isUpgradeAvailable(upgrade: Upgrade, ctx: OfferContext): boolean {
  if (!isStackable(upgrade) && ctx.ownedIds.has(upgrade.id)) return false;

  switch (upgrade.requires) {
    case undefined: return true;
    case 'captured': return ctx.capturedCount > 0;
    case 'queen': return (ctx.pieceCounts.get('q') || 0) > 0;
    case 'rook': return (ctx.pieceCounts.get('r') || 0) > 0;
    case 'bishop': return (ctx.pieceCounts.get('b') || 0) > 0;
    case 'knight': return (ctx.pieceCounts.get('n') || 0) > 0;
    case 'pawn': return (ctx.pieceCounts.get('p') || 0) > 0;
    case 'bishops2': return (ctx.pieceCounts.get('b') || 0) >= 2;
    case 'obstacles': return ctx.floor >= 3;
    case 'teleporters': return ctx.floor >= 5 || ctx.ownedIds.has('place_teleporter');
    case 'puzzles': return ctx.puzzlesRemaining;
    default: return true;
  }
}

/**
 * Rarity weights per reward tier. Elites and bosses roll meaningfully better.
 */
const TIER_WEIGHTS: Record<'normal' | 'elite' | 'boss', [number, number, number, number]> = {
  normal: [60, 25, 12, 3],
  elite: [20, 45, 27, 8],
  boss: [0, 30, 45, 25],
};

const RARITIES = ['common', 'uncommon', 'rare', 'legendary'] as const;

/**
 * Roll unique upgrade offers filtered by run context, with tier-based rarity
 * weighting. This is the main entry point for reward and shop rolls.
 */
export function getUpgradeOffers(count: number, ctx: OfferContext): Upgrade[] {
  const available = Object.values(UPGRADE_LIBRARY).filter(u => isUpgradeAvailable(u, ctx));
  const weights = TIER_WEIGHTS[ctx.tier || 'normal'];
  const offers: Upgrade[] = [];

  const poolFor = (rarity: typeof RARITIES[number]): Upgrade[] =>
    available.filter(u => u.rarity === rarity && !offers.find(o => o.id === u.id));

  let guard = 0;
  while (offers.length < count && guard++ < 200) {
    const roll = Math.random() * 100;
    let acc = 0;
    let rarityIndex = 0;
    for (let i = 0; i < RARITIES.length; i++) {
      acc += weights[i];
      if (roll < acc) { rarityIndex = i; break; }
    }

    // Find a non-empty pool at the rolled rarity, falling back downward then upward
    let pool: Upgrade[] = [];
    for (const i of [rarityIndex, rarityIndex - 1, rarityIndex - 2, rarityIndex - 3, rarityIndex + 1, rarityIndex + 2, rarityIndex + 3]) {
      if (i < 0 || i >= RARITIES.length) continue;
      pool = poolFor(RARITIES[i]);
      if (pool.length > 0) break;
    }
    if (pool.length === 0) break; // nothing left to offer at all

    offers.push(pool[Math.floor(Math.random() * pool.length)]);
  }

  return offers;
}

/**
 * Get upgrade by ID
 */
export function getUpgradeById(id: string): Upgrade | undefined {
  return UPGRADE_LIBRARY[id];
}

/**
 * Get upgrade display color based on rarity
 */
export function getUpgradeColor(rarity: string): string {
  switch (rarity) {
    case 'common':
      return '#9e9e9e'; // Gray
    case 'uncommon':
      return '#4caf50'; // Green
    case 'rare':
      return '#2196f3'; // Blue
    case 'legendary':
      return '#ff9800'; // Orange/Gold
    default:
      return '#ffffff';
  }
}

// Legacy compatibility - maintain old PieceUpgrade interface for now
export function convertToPieceUpgrade(upgrade: Upgrade): PieceUpgrade {
  return {
    id: upgrade.id,
    name: upgrade.name,
    description: upgrade.description,
    effect: {
      type: upgrade.effect.type as any,
      value: upgrade.effect.value,
      ...(upgrade.effect.target && { target: upgrade.effect.target }),
    },
  };
}